import React, { useEffect } from "react";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { Icon } from "@iconify/react";
import { Button, Card, List, Popconfirm, Space, Table, Tooltip } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import type { ColumnsType } from "antd/es/table";

import TenantActionSelect from "js/components/Tenants/TenantActionSelect";
import { PaginationTable, TenantPerson } from "js/components/types";

import "styles/components/Dashboard/TableDashboard.scss";

import { useDeleteTenant } from "../../../hooks/useDeleteTenant";
import { toast } from "../../../utils/toast";

interface Props {
  data: TenantPerson[];
  setData: (data: TenantPerson[]) => void;
  filteredData: TenantPerson[];
  setFilteredData: (data: TenantPerson[]) => void;
  setPagination: (pagination: PaginationTable) => void;
  pagination: PaginationTable;
  loading: boolean;
  fromOrganization: boolean;
  onEditTenant?: (tenant: TenantPerson) => void;
}

const TenantsTable: React.FC<Props> = ({
  data,
  setData,
  filteredData,
  setFilteredData,
  pagination,
  setPagination,
  loading,
  fromOrganization = false,
  onEditTenant,
}) => {
  const screen = useBreakpoint();
  const deleteTenantMutation = useDeleteTenant();

  const handleDeleteTenant = (tenant: any) => {
    deleteTenantMutation.mutate(tenant.id, {
      onSuccess: () => {
        toast.success("Inquilino eliminado exitosamente");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Error al eliminar inquilino",
        );
      },
    });
  };

  const columns: ColumnsType<TenantPerson> = [
    {
      title: "Fecha de Creación",
      dataIndex: "created_at",
      render: (date) =>
        new Date(date).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      align: "center",
      width: "4%",
    },
    {
      title: "Nombre",
      dataIndex: "name",
      render: (name, tenant) => (
        <span className="sentry-mask">
          {`${name} ${tenant.first_surname} ${tenant.second_surname}`}
        </span>
      ),
      width: "8%",
    },
    {
      title: "Correo",
      dataIndex: "email",
      render: (email) => <span className="sentry-mask">{email}</span>,
      width: "5%",
    },
    {
      title: "Tipo",
      dataIndex: "legal_type",
      align: "center",
      render: (value, tenant) => {
        if (value == "Natural") return "Persona Física";
        else return "Persona Moral";
      },
      width: "5%",
    },
  ];

  if (!fromOrganization)
    columns.push({
      title: "Acciones",
      dataIndex: "actions",
      align: "center",
      render: (actions, tenant) => (
        <TenantActionSelect
          tenant={tenant}
          data={data}
          setData={setData}
          onEdit={onEditTenant}
        />
      ),
      width: "5%",
    });

  const handleTableChange = (pagination: PaginationTable) => {
    setPagination(pagination);
  };

  useEffect(() => {
    if (data.length) {
      setFilteredData(data);
    }
  }, [data]);

  return (
    <>
      {!screen.md ? (
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 4,
            lg: 4,
            xl: 4,
            xxl: 3,
          }}
          dataSource={filteredData?.length ? filteredData : data}
          renderItem={(item) => (
            <List.Item>
              <Card
                title={
                  <span className="sentry-mask">
                    {item.name} {item.first_surname} {item.second_surname}
                  </span>
                }
                actions={
                  fromOrganization
                    ? []
                    : [
                        <Tooltip title="Editar">
                          <Button
                            size={"small"}
                            type={"link"}
                            onClick={() => onEditTenant && onEditTenant(item)}
                          >
                            <Icon fontSize={20} icon="akar-icons:edit" />
                          </Button>
                        </Tooltip>,

                        <Tooltip title="Eliminar">
                          <Popconfirm
                            title="Eliminar Inquilino"
                            description="¿Seguro que deseas eliminar?"
                            onConfirm={() => handleDeleteTenant(item)}
                            icon={
                              <QuestionCircleOutlined
                                style={{ color: "red" }}
                                rev={undefined}
                              />
                            }
                            okText={"Ok"}
                            cancelText={"Cancelar"}
                          >
                            <Button
                              danger
                              size={"small"}
                              type={"link"}
                              loading={deleteTenantMutation.isPending}
                            >
                              <Icon fontSize={20} icon="bi:trash3" />
                            </Button>
                          </Popconfirm>
                        </Tooltip>,
                      ]
                }
              >
                <Space direction={"vertical"}>
                  <div>
                    <strong>Tipo de persona:</strong> {item.legal_type}
                  </div>
                  <div>
                    <strong>Correo:</strong> <span className="sentry-mask">{item.email}</span>
                  </div>
                  <div>
                    <strong>Creado:</strong>{" "}
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString("es-ES", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : ""}
                  </div>
                </Space>
              </Card>
            </List.Item>
          )}
        />
      ) : (
        <>
          <Table
            size={"small"}
            columns={columns}
            rowKey={(record) => record.id ?? ""}
            dataSource={filteredData?.length ? filteredData : data}
            pagination={pagination}
            onChange={handleTableChange}
            loading={loading}
            scroll={{ y: 750, x: "max-content" }}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    style={{
                      textAlign:
                        props.children.includes("Acciones") ||
                        props.children.includes("Tipo") ||
                        props.children.includes("Fecha de Creación")
                          ? "center"
                          : undefined,
                    }}
                  />
                ),
              },
            }}
          />
        </>
      )}
    </>
  );
};

export default TenantsTable;
